import { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from '../../hooks/useTranslation';

/**
 * SnaLoader — 3-stage animated bottom sheet for SNA (Silent Network Auth)
 *
 * Stages:
 *   1. 'checking'   — "Checking your mobile network..."   (1600ms)
 *   2. 'confirming' — "Confirming your number..."          (1900ms)
 *   3. 'success'    — "Verification successful"            (1400ms) → onDone()
 *   or 'fail'       — "Network verification failed..."    (1800ms) → onFail()
 *
 * Props:
 *   open         {boolean}   Whether the sheet is visible
 *   outcome      {string}    'success' | 'fail' — injected by parent when SNA resolves
 *   onDone       {function}  Called after success animation completes
 *   onFail       {function}  Called after fail animation completes
 *   primaryColor {string}    Entity primary color
 *   customDetails {object}
 */
export default function SnaLoader({
	open,
	outcome,
	onDone,
	onFail,
	primaryColor = '#3053D3',
	customDetails,
}) {
	const { t } = useTranslation();
	const [stage, setStage] = useState('checking');
	const timersRef = useRef([]);
	const stageRef = useRef('checking');

	const isDark = customDetails?.darkTheme === true;

	const DURATIONS = {
		checking: 1600,
		confirming: 1900,
		success: 1400,
		fail: 1800,
	};

	const addTimer = (fn, delay) => {
		const id = setTimeout(fn, delay);
		timersRef.current.push(id);
	};

	const clearTimers = () => {
		timersRef.current.forEach(clearTimeout);
		timersRef.current = [];
	};

	const setStageSync = (s) => {
		stageRef.current = s;
		setStage(s);
	};

	// Start the animation pipeline when opened
	useEffect(() => {
		if (!open) {
			clearTimers();
			setStageSync('checking');
			return;
		}
		setStageSync('checking');

		// checking → confirming
		addTimer(() => {
			if (stageRef.current !== 'fail') setStageSync('confirming');
		}, DURATIONS.checking);

		// confirming → success (unless fail was triggered)
		addTimer(() => {
			if (stageRef.current !== 'fail') setStageSync('success');
		}, DURATIONS.checking + DURATIONS.confirming);

		// success → onDone
		addTimer(
			() => {
				if (stageRef.current === 'success' && onDone) onDone();
			},
			DURATIONS.checking + DURATIONS.confirming + DURATIONS.success,
		);

		return clearTimers;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	// If parent sends outcome='fail', override the staged animation
	useEffect(() => {
		if (!open || outcome !== 'fail') return;
		clearTimers();
		setStageSync('fail');
		addTimer(() => {
			if (onFail) onFail();
		}, DURATIONS.fail);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [outcome]);

	if (!open) return null;

	const isSuccess = stage === 'success';
	const isFail = stage === 'fail';

	const stageText = {
		checking: t('snaLoader.checking') || 'Checking mobile network...',
		confirming:
			t('snaLoader.confirming') || 'Securely confirming your number...',
		success: t('snaLoader.success') || 'Verification successful',
		fail: t('snaLoader.fail') || 'Verification failed. Please verify using OTP',
	}[stage];

	return (
		<Box
			role="status"
			aria-live="polite"
			aria-busy={!isSuccess && !isFail}
			sx={{
				position: 'fixed',
				bottom: 0,
				left: 0,
				right: 0,
				zIndex: 1400,
			}}
		>
			{/* Dimmed backdrop */}
			<Box
				sx={{
					position: 'fixed',
					inset: 0,
					background: 'rgba(0,0,0,0.35)',
					zIndex: -1,
				}}
			/>

			{/* Bottom sheet card */}
			<Box
				sx={{
					backgroundColor: isDark ? '#1E1E1E' : '#FFFFFF',
					borderRadius: '20px 20px 0 0',
					px: 3,
					pt: 1.5,
					pb: 5,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: '1.5rem',
					boxShadow: '0px -4px 24px rgba(0,0,0,0.12)',
				}}
			>
				{/* Drag indicator */}
				<Box
					sx={{
						width: '40px',
						height: '4px',
						borderRadius: '2px',
						backgroundColor: isDark ? '#555' : '#E0E0E0',
						mt: 0.5,
					}}
				/>

				{/* Icon area */}
				<Box
					sx={{
						width: '5rem',
						height: '5rem',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					{isSuccess ? (
						<CheckCircleIcon
							sx={{
								fontSize: '5rem',
								color: primaryColor,
								animation: 'sna-pop 0.35s cubic-bezier(0.34,1.56,0.64,1)',
								'@keyframes sna-pop': {
									'0%': { transform: 'scale(0.5)', opacity: 0 },
									'100%': { transform: 'scale(1)', opacity: 1 },
								},
							}}
						/>
					) : isFail ? (
						<Box
							sx={{
								width: '5rem',
								height: '5rem',
								borderRadius: '50%',
								backgroundColor: '#FFF3F3',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							<Typography sx={{ fontSize: '2.5rem', lineHeight: 1 }}>
								⚠️
							</Typography>
						</Box>
					) : (
						<CircularProgress
							size={72}
							thickness={3}
							sx={{
								color: primaryColor,
								'& .MuiCircularProgress-circle': {
									strokeLinecap: 'round',
								},
							}}
						/>
					)}
				</Box>

				{/* Stage text */}
				<Typography
					sx={{
						fontSize: '1rem',
						fontWeight: 500,
						textAlign: 'center',
						color: isDark ? '#FFFFFF' : '#111111',
						fontFamily: customDetails?.fontFamily,
						lineHeight: 1.6,
						px: 2,
					}}
				>
					{stageText}
				</Typography>
			</Box>
		</Box>
	);
}
